import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_ENDPOINTS } from "../../api/config";
import { useUser } from "../../user_context";
import JSLayout from "./js_layout";
import "../employer/css/emp_layout.css";

const IMAGE_BASE_URL = window.location.hostname === "localhost" 
  ? "http://localhost:4000" 
  : window.location.origin;

// --- SUB-COMPONENT FOR AUTO-SCROLLING IMAGES ---
function AutoImageSlider({ images, listingType, title }) {
  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    if (!images || images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIdx((prevIdx) => (prevIdx + 1) % images.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [images]);

  if (!images || images.length === 0) {
    return (
      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "0.25rem", background: "#f5f5f7" }}>
        <span style={{ fontSize: "1.8rem" }}>{listingType === "RentalSpace" ? "🏢" : "🧴"}</span>
        <span style={{ fontSize: "0.7rem", color: "#8e8e93" }}>No image uploaded</span>
      </div>
    );
  }

  const resolveImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const sanitizedUrl = url.startsWith("/") ? url : `/${url}`;
    return `${IMAGE_BASE_URL}${sanitizedUrl}`;
  };

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      {images.map((imgUrl, idx) => (
        <img
          key={idx}
          src={resolveImageUrl(imgUrl)} 
          alt={`${title} - look ${idx + 1}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            position: "absolute",
            top: 0,
            left: 0,
            opacity: currentIdx === idx ? 1 : 0,
            transition: "opacity 0.8s ease-in-out",
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "https://placehold.co/400x300/f0f0f2/aaaaaa?text=Asset+Image";
          }}
        />
      ))}
      
      {images.length > 1 && (
        <div style={{ position: "absolute", bottom: "8px", left: "0", right: "0", display: "flex", justifyContent: "center", gap: "4px", zIndex: 2 }}>
          {images.map((_, idx) => (
            <div
              key={idx}
              style={{
                width: "5px",
                height: "5px",
                borderRadius: "50%",
                background: currentIdx === idx ? "var(--color-primary-light)" : "rgba(0,0,0,0.2)",
                transition: "background 0.3s"
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// --- MAIN MARKETPLACE LANDING COMPONENTS ---
export default function JSMarketplaceLanding() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState("All");
  
  // Pagination mechanics
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchData = async (pageNumber, currentTab, isInitial = false) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        `${API_ENDPOINTS.MARKETPLACE}/listings?page=${pageNumber}&type=${currentTab}`, 
        { headers: { "Authorization": `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setHasMore(data.hasMore);
        
        if (isInitial) {
          setListings(data.listings || []);
        } else {
          setListings(prev => [...prev, ...(data.listings || [])]);
        }
      }
    } catch (err) {
      console.error("Failed loading rows:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setPage(1);
    fetchData(1, activeTab, true);
  }, [activeTab]);

  useEffect(() => {
    const handleScroll = () => {
      if (loading || loadingMore || !hasMore) return;
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 200) {
        setLoadingMore(true);
        const nextPage = page + 1;
        setPage(nextPage);
        fetchData(nextPage, activeTab, false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [page, hasMore, loading, loadingMore, activeTab]);

  return (
    <JSLayout title="B2B Beauty" titleEm="Marketplace ◈">
      <div className="emd-fade" style={{ position: "relative", minHeight: "80vh", background: "transparent", padding: "0.5rem", borderRadius: "8px" }}>
        
        {/* Compact Head Segment Options */}
        <div className="emd-section-hdr" style={{ marginTop: "1rem", borderBottom: "1px solid #e9ecef", paddingBottom: "1rem" }}>
          <div className="emd-section-title" style={{ color: "#212529", fontWeight: "600" }}>Explore <em>listings</em></div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {["All", "Product", "RentalSpace"].map((tab) => (
              <button
                key={tab}
                className={`emd-listing-btn ${activeTab === tab ? "primary" : ""}`}
                style={{ 
                  borderRadius: "20px", 
                  padding: "0.4rem 1.2rem", 
                  fontSize: "0.85rem",
                  background: activeTab === tab ? "var(--color-primary-dark)" : "#fff",
                  color: activeTab === tab ? "#fff" : "#495057",
                  border: activeTab === tab ? "1px solid var(--color-primary-dark)" : "1px solid #ced4da",
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                }}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "RentalSpace" ? "Spaces 🏢" : tab === "Product" ? "Products 🧴" : "All Items"}
              </button>
            ))}
          </div>
        </div>

        {loading && page === 1 ? (
          <div className="emd-loading-container"><div className="si-spinner"></div></div>
        ) : listings.length === 0 ? (
          /* --- REFRESHING EMPTY STATE UI --- */
          <div style={{ 
            display: "flex", 
            flexDirection: "column", 
            alignItems: "center", 
            justifyContent: "center", 
            textAlign: "center",
            padding: "4rem 2rem",
            marginTop: "3rem",
            background: "#ffffff",
            border: "1px dashed #cbd5e1",
            borderRadius: "12px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.02)"
          }}>
            <div style={{ fontSize: "3.5rem", marginBottom: "1rem", animation: "pulse 2s infinite" }}>✨</div>
            <h3 style={{ color: "#1e293b", fontSize: "1.3rem", fontWeight: "600", margin: "0 0 0.5rem 0" }}>
              The showroom is quiet right now
            </h3>
            <p style={{ color: "#64748b", fontSize: "0.9rem", maxWidth: "400px", margin: "0 0 1.5rem 0", lineHeight: "1.5" }}>
              {activeTab === "All" 
                ? "There are no active catalog items available at the moment. Check back shortly or be the first to list yours!"
                : `No active items posted under the "${activeTab === "RentalSpace" ? "Spaces" : "Products"}" collection right now.`}
            </p>
            <div style={{ display: "flex", gap: "1rem" }}>
              <button 
                onClick={() => fetchData(1, activeTab, true)}
                style={{
                  padding: "0.5rem 1.2rem",
                  fontSize: "0.85rem",
                  background: "#f1f5f9",
                  color: "#334155",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "background 0.2s"
                }}
              >
                🔄 Refresh Page
              </button>
              
            </div>
          </div>
        ) : (
          /* COMPACT CARD GRID STRUCTURE - LIGHT STYLE */
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", 
            gap: "1.25rem", 
            marginTop: "1.5rem" 
          }}>
            {listings.map((listing) => (
              <div 
                key={listing.id} 
                className="emd-listing-card" 
                style={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  overflow: "hidden", 
                  padding: "0", 
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)",
                  transition: "transform 0.2s, box-shadow 0.2s"
                }}
              >
                {/* Visual Image Header Box */}
                <div style={{ width: "100%", height: "140px", background: "#f1f5f9", position: "relative", overflow: "hidden" }}>
                  <AutoImageSlider 
                    images={listing.images} 
                    listingType={listing.listingType} 
                    title={listing.title} 
                  />

                  {/* Floating Tag Type Badge */}
                  <span style={{
                    position: "absolute", top: "8px", left: "8px", 
                    background: listing.listingType === "RentalSpace" ? "#fef3c7" : "#dcfce7",
                    color: listing.listingType === "RentalSpace" ? "#b45309" : "#15803d", 
                    padding: "0.2rem 0.5rem", borderRadius: "4px", fontSize: "0.7rem", fontWeight: "bold",
                    border: listing.listingType === "RentalSpace" ? "1px solid #f59e0b" : "1px solid #22c55e",
                    zIndex: 3
                  }}>
                    {listing.listingType === "RentalSpace" ? "Space" : "Product"}
                  </span>
                </div>

                {/* Info Text Compartment Layer */}
                <div style={{ padding: "0.85rem", flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <div>
                    <h4 style={{ color: "#1e293b", fontSize: "0.95rem", margin: "0 0 0.25rem 0", fontWeight: "600", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {listing.title}
                    </h4>
                    
                    <p style={{ color: "#64748b", fontSize: "0.8rem", margin: "0 0 0.75rem 0", lineHeight: "1.4" }}>
                      {listing.description.length > 65 ? `${listing.description.substring(0, 65)}...` : listing.description}
                    </p>
                  </div>

                  <div>
                    <div style={{ height: "1px", background: "#f1f5f9", margin: "0 0 0.5rem 0" }} />
                    
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
                      <div>
                        <div style={{ fontSize: "1rem", fontWeight: "700", color: "#1e293b" }}>
                          KES {listing.price.toLocaleString()}
                        </div>
                        {listing.listingType === "RentalSpace" && (
                          <div style={{ fontSize: "0.65rem", color: "#b45309", marginTop: "-1px", fontWeight: "500" }}>
                            per {listing.rentalFrequency?.toLowerCase() || "month"}
                          </div>
                        )}
                      </div>

                      <button 
                        onClick={() => navigate(`/marketplace/details/${listing.id}`)}
                        style={{ 
                          padding: "0.4rem 0.8rem", 
                          fontSize: "0.75rem", 
                          whiteSpace: "nowrap",
                          background: "var(--color-primary-dark)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          fontWeight: "500",
                          cursor: "pointer"
                        }}
                      >
                        View More
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}

        {loadingMore && <div style={{ textAlign: "center", padding: "1.5rem", color: "#64748b", fontSize: "0.85rem" }}>Syncing more items...</div>}

        {/* Floating Add Action Button */}
        <button 
          onClick={() => navigate("/marketplace/create")}
          style={{
            position: "fixed", bottom: "30px", right: "30px",
            background: "linear-gradient(135deg, var(--color-primary-dark) 0%, #8a620d 100%)",
            color: "#fff", border: "none", borderRadius: "50px", padding: "0.8rem 1.5rem",
            fontSize: "0.95rem", fontWeight: "bold", boxShadow: "0px 4px 12px rgba(170,124,17,0.3)",
            cursor: "pointer", zIndex: 1000, display: "flex", alignItems: "center", gap: "0.4rem"
          }}
        >
          💰 Sell Item
        </button>

      </div>
    </JSLayout>
  );
}